import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { ParentReportModal } from "../components/reports/ParentReportModal";
import { TeamBuilderPanel } from "../components/teambuilder";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  TrophyIcon,
  UserIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

const SkillBasedResults = () => {
  const { evalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [playerResults, setPlayerResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("average_score");
  const [sortOrder, setSortOrder] = useState("desc");
  const [positionFilter, setPositionFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const groupDropdownRef = useRef(null);

  // Drill-down state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPlayerDetails, setShowPlayerDetails] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Parent report state
  const [showParentReport, setShowParentReport] = useState(false);
  const [parentReportPlayer, setParentReportPlayer] = useState(null);

  // Team builder state
  const [showTeamBuilder, setShowTeamBuilder] = useState(false);

  // Feature flags
  const [featureFlags, setFeatureFlags] = useState(null); // null = all enabled

  // Send parent reports state
  const [showSendReports, setShowSendReports] = useState(false);
  const [sendingReports, setSendingReports] = useState(false);

  // Results tabs and calibration dashboard state
  const [activeTab, setActiveTab] = useState("results");
  const [scoreMode, setScoreMode] = useState("raw");
  const [showNormalizeConfirm, setShowNormalizeConfirm] = useState(false);
  const [normalizingScores, setNormalizingScores] = useState(false);
  const [calibrationData, setCalibrationData] = useState(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);
  const [calibrationError, setCalibrationError] = useState("");

  const loadData = useCallback(async () => {
    try {
      const [evalData, resultsData, categoriesData, skillsData] =
        await Promise.all([
          apiService.getEvaluation(evalId),
          apiService.getSkillBasedResults(evalId),
          apiService.getSkillCategoriesForEvaluation(evalId),
          apiService.getSkills(evalId),
        ]);

      setEvaluation(evalData);
      setPlayerResults(resultsData);
      setCategories(categoriesData);
      setSkills(skillsData);
    } catch (error) {
      console.error("Error loading skill-based results:", error);
      toast.error("Failed to load evaluation results");
    }
    setLoading(false);
  }, [evalId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!evaluation?.club_id) return;
    apiService
      .getClubSubscriptionLimits(evaluation.club_id)
      .then((limits) => setFeatureFlags(limits.feature_flags ?? null))
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [evaluation?.club_id]);

  const canViewCalibration =
    ["club_admin", "super_admin"].includes(user?.role) &&
    evaluation?.status === "completed";

  const loadCalibrationData = useCallback(async () => {
    if (!canViewCalibration || calibrationData || calibrationLoading) return;

    setCalibrationLoading(true);
    setCalibrationError("");
    try {
      const data = await apiService.getEvaluationCalibration(evalId);
      setCalibrationData(data);
    } catch (error) {
      console.error("Error loading calibration dashboard:", error);
      setCalibrationError("Failed to load calibration data");
      toast.error("Failed to load calibration data");
    } finally {
      setCalibrationLoading(false);
    }
  }, [calibrationData, calibrationLoading, canViewCalibration, evalId]);

  useEffect(() => {
    if (activeTab === "calibration" && canViewCalibration) {
      loadCalibrationData();
    }
  }, [activeTab, canViewCalibration, loadCalibrationData]);

  useEffect(() => {
    if (!canViewCalibration && activeTab === "calibration") {
      setActiveTab("results");
    }
  }, [activeTab, canViewCalibration]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target)
      ) {
        setShowGroupDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter and sort players
  const filteredAndSortedPlayers = playerResults
    .filter((player) => {
      const matchesSearch =
        player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.number?.toString().includes(searchTerm);
      const matchesPosition =
        !positionFilter || player.position === positionFilter;
      const matchesCategory =
        !categoryFilter ||
        (player.category_scores &&
          Object.keys(player.category_scores).includes(categoryFilter));
      const matchesGroup =
        selectedGroups.length === 0 ||
        (player.group && selectedGroups.includes(player.group));
      return (
        matchesSearch && matchesPosition && matchesCategory && matchesGroup
      );
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "number":
          aValue = parseInt(a.number) || 0;
          bValue = parseInt(b.number) || 0;
          break;
        case "position":
          aValue = a.position || "";
          bValue = b.position || "";
          break;
        case "completion_percentage":
          aValue = a.completion_percentage || 0;
          bValue = b.completion_percentage || 0;
          break;
        case "total_skills":
          aValue = a.total_skills || 0;
          bValue = b.total_skills || 0;
          break;
        case "total_score":
          aValue = getDisplayScore(a, "total_score");
          bValue = getDisplayScore(b, "total_score");
          break;
        default: // average_score
          aValue = getDisplayScore(a, "average_score");
          bValue = getDisplayScore(b, "average_score");
      }

      if (typeof aValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

  function getDisplayScore(record, field) {
    if (scoreMode === "normalized") {
      const normalizedField = field === "numeric_score" ? "normalized_score" : `normalized_${field}`;
      if (record?.[normalizedField] !== null && record?.[normalizedField] !== undefined) {
        return record[normalizedField];
      }
    }
    return record?.[field] || 0;
  }

  const getScoreColor = (score, maxScore = 5) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-green-600 bg-green-50";
    if (percentage >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const getBadgeColor = (score, maxScore = 5) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "green";
    if (percentage >= 60) return "yellow";
    return "red";
  };

  // Get max score for a category (average of all skills in that category)
  const getCategoryMaxScore = (categoryId) => {
    const categorySkills = skills.filter((s) => s.category_id === categoryId);
    if (categorySkills.length === 0) return 5;

    const maxScores = categorySkills.map((skill) => {
      if (skill?.rating_scales?.scale_type === "pass_fail") {
        return 10;
      }
      return skill?.rating_scales?.max_value || 5;
    });

    // Return average of max scores for the category
    return maxScores.reduce((sum, max) => sum + max, 0) / maxScores.length;
  };

  // Get overall max score (average of all skills)
  const getOverallMaxScore = () => {
    if (skills.length === 0) return 5;

    const maxScores = skills.map((skill) => {
      if (skill?.rating_scales?.scale_type === "pass_fail") {
        return 10;
      }
      return skill?.rating_scales?.max_value || 5;
    });

    return maxScores.reduce((sum, max) => sum + max, 0) / maxScores.length;
  };

  const getPositions = () => {
    return [
      ...new Set(playerResults.map((p) => p.position).filter(Boolean)),
    ].sort();
  };

  const getGroups = () => {
    const allGroups = [];
    playerResults.forEach((player) => {
      if (player.group) {
        if (allGroups.find((g) => g === player.group)) return;
        allGroups.push(player.group);
      }
    });
    return allGroups.sort();
  };

  const handleGroupToggle = (groupName) => {
    setSelectedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((name) => name !== groupName)
        : [...prev, groupName],
    );
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    setShowPlayerDetails(true);
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const playersWithEmails = playerResults.filter(
    (p) =>
      (p.email && p.email.trim()) ||
      (p.parent_emails && p.parent_emails.length > 0),
  );

  const handleNormalizeScores = async () => {
    setNormalizingScores(true);
    try {
      const result = await apiService.normalizeEvaluationScores(evalId);
      toast.success(`Normalized ${result.normalized_scores || 0} scores`);
      setShowNormalizeConfirm(false);
      const refreshedCalibration = await apiService.getEvaluationCalibration(evalId);
      setCalibrationData(refreshedCalibration);
      await loadData();
    } catch (error) {
      console.error("Error normalizing scores:", error);
      toast.error("Failed to normalize scores");
    } finally {
      setNormalizingScores(false);
    }
  };

  const handleSendParentReports = async () => {
    setSendingReports(true);
    try {
      const result = await apiService.sendParentReports(evalId);
      if (result.sent > 0) {
        toast.success(
          `Sent ${result.sent} report email${result.sent !== 1 ? "s" : ""}`,
        );
      }
      if (result.failed > 0) {
        toast.error(
          `${result.failed} email${result.failed !== 1 ? "s" : ""} failed to send`,
        );
      }
      if (result.sent === 0 && result.failed === 0) {
        toast("No players have email addresses", { icon: "ℹ️" });
      }
    } catch (error) {
      console.error("Error sending parent reports:", error);
      toast.error("Failed to send parent reports");
    }
    setSendingReports(false);
    setShowSendReports(false);
  };

  const formatScore = (value) =>
    typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : "0.00";

  const getMeanDifference = (mean) => {
    const sessionMean = calibrationData?.session_stats?.mean || 0;
    const difference = mean - sessionMean;
    if (Math.abs(difference) < 0.005) return "Even with session average";
    return `${difference > 0 ? "+" : ""}${difference.toFixed(2)} vs session average`;
  };

  const renderCalibrationDashboard = () => {
    if (calibrationLoading) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-gray-600">
            Loading calibration data...
          </CardContent>
        </Card>
      );
    }

    if (calibrationError) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-600 font-medium">{calibrationError}</p>
            <Button className="mt-4" variant="outline" onClick={loadCalibrationData}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      );
    }

    const sessionStats = calibrationData?.session_stats || {
      mean: 0,
      std_dev: 0,
      total_scores: 0,
    };
    const evaluators = calibrationData?.evaluators || [];

    if (!sessionStats.total_scores || evaluators.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center text-gray-600">
            No numeric scores exist for this completed evaluation yet.
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={() => setShowNormalizeConfirm(true)} disabled={normalizingScores}>
            {normalizingScores ? "Normalizing..." : "Normalize Scores"}
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Session Average</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatScore(sessionStats.mean)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="text-gray-500">Std. Dev.</p>
                  <p className="font-semibold text-gray-900">
                    {formatScore(sessionStats.std_dev)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Numeric Scores</p>
                  <p className="font-semibold text-gray-900">
                    {sessionStats.total_scores}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {evaluators.map((evaluator) => {
                const histogram = evaluator.histogram || [];
                const maxBucket = Math.max(...histogram, 1);

                return (
                  <div key={evaluator.evaluator_id} className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {evaluator.evaluator_name}
                          </h3>
                          {evaluator.is_outlier && <Badge variant="red">Outlier</Badge>}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {getMeanDifference(evaluator.mean)}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-6 text-sm lg:text-right">
                        <div>
                          <p className="text-gray-500">Mean Score</p>
                          <p className="font-semibold text-gray-900">
                            {formatScore(evaluator.mean)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Score Count</p>
                          <p className="font-semibold text-gray-900">
                            {evaluator.score_count}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5">
                      <div className="flex items-end gap-2 h-24 border-b border-gray-200 pb-1">
                        {histogram.map((count, index) => (
                          <div
                            key={index}
                            className="flex-1 flex flex-col justify-end items-center h-full"
                            title={`Bucket ${index + 1}: ${count} score${count === 1 ? "" : "s"}`}
                          >
                            <span className="text-xs text-gray-500 mb-1">
                              {count || ""}
                            </span>
                            <div
                              className="w-full bg-slate-500 rounded-t min-h-[4px]"
                              style={{
                                height: `${Math.max((count / maxBucket) * 100, count ? 8 : 0)}%`,
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Low</span>
                        <span>Score distribution</span>
                        <span>High</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const exportResults = () => {
    const csvData = [];
    csvData.push([
      "Player Name",
      "Number",
      "Position",
      "Average Score",
      "Total Score",
      "Completion %",
      "Skills Completed",
      "Total Skills",
      ...categories.map((cat) => `${cat.name} Avg`),
    ]);

    filteredAndSortedPlayers.forEach((player) => {
      const categoryScores = categories.map((cat) => {
        const categoryData = player.category_scores?.[cat.id];
        return categoryData
          ? (getDisplayScore(categoryData, "score") / categoryData.skills_completed).toFixed(2)
          : "N/A";
      });

      csvData.push([
        player.name,
        player.number || "",
        player.position || "",
        getDisplayScore(player, "average_score").toFixed(2),
        getDisplayScore(player, "total_score").toFixed(2),
        (player.completion_percentage || 0).toFixed(1),
        player.completed_skills || 0,
        player.total_skills || 0,
        ...categoryScores,
      ]);
    });

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `skill-results-${evaluation?.name || "results"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success("Results exported successfully!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {evaluation?.name} - Skill Results
          </h1>
          <div className="flex items-center space-x-4 mt-2">
            <Badge variant="blue">
              {playerResults.length} Player
              {playerResults.length !== 1 ? "s" : ""}
            </Badge>
            <Badge variant="green">
              {categories.length} Categor{categories.length !== 1 ? "ies" : "y"}
            </Badge>
            <Badge variant="purple">{skills.length} Skills</Badge>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                `/portal/club/${evaluation?.club_id}/evaluation/${evalId}`,
              )
            }
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Details
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="w-4 h-4 mr-2" />
            Filters
          </Button>
          {(() => {
            const allowed =
              !featureFlags || featureFlags.allow_team_builder !== false;
            return (
              <div className="relative group">
                <Button
                  variant="outline"
                  onClick={allowed ? () => setShowTeamBuilder(true) : undefined}
                  disabled={!allowed}
                >
                  <UserGroupIcon className="w-4 h-4 mr-2" />
                  Team Builder
                </Button>
                {!allowed && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                    Team Builder is not included in your current plan. Upgrade
                    to use team builder.
                  </div>
                )}
              </div>
            );
          })()}
          {evaluation?.status === "completed" &&
            (() => {
              const allowed =
                !featureFlags || featureFlags.allow_parent_reports !== false;
              return (
                <div className="relative group">
                  <Button
                    variant="outline"
                    onClick={allowed ? () => setShowSendReports(true) : undefined}
                    disabled={!allowed}
                  >
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    Send Parent Reports
                  </Button>
                  {!allowed && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                      Parent reports are not included in your current plan. Upgrade to send parent reports.
                    </div>
                  )}
                </div>
              );
            })()}
          {canViewCalibration && (
            <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Score display mode">
              <button
                type="button"
                className={`px-3 py-2 text-sm font-medium border rounded-l-md ${
                  scoreMode === "raw"
                    ? "bg-slate-600 text-white border-slate-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => setScoreMode("raw")}
              >
                Raw
              </button>
              <button
                type="button"
                className={`px-3 py-2 text-sm font-medium border border-l-0 rounded-r-md ${
                  scoreMode === "normalized"
                    ? "bg-slate-600 text-white border-slate-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => setScoreMode("normalized")}
              >
                Normalized
              </button>
            </div>
          )}
          <Button onClick={exportResults}>
            <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Results Tabs */}
      {canViewCalibration && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Results tabs">
            <button
              type="button"
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "results"
                  ? "border-slate-500 text-slate-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              onClick={() => setActiveTab("results")}
            >
              Results
            </button>
            <button
              type="button"
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "calibration"
                  ? "border-slate-500 text-slate-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
              onClick={() => setActiveTab("calibration")}
            >
              <span className="inline-flex items-center">
                <ChartBarIcon className="w-4 h-4 mr-2" />
                Calibration
              </span>
            </button>
          </nav>
        </div>
      )}

      {activeTab === "calibration" ? (
        renderCalibrationDashboard()
      ) : (
        <>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-slate-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Players
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {playerResults.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrophyIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {playerResults.length > 0
                    ? (
                        playerResults.reduce(
                          (sum, p) => sum + getDisplayScore(p, "average_score"),
                          0,
                        ) / playerResults.length
                      ).toFixed(1)
                    : "0.0"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Avg Completion
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {playerResults.length > 0
                    ? (
                        playerResults.reduce(
                          (sum, p) => sum + (p.completion_percentage || 0),
                          0,
                        ) / playerResults.length
                      ).toFixed(1)
                    : "0.0"}
                  %
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 font-semibold text-sm">S</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Skills
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {skills.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name or number..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="average_score">Average Score</option>
                    <option value="total_score">Total Score</option>
                    <option value="completion_percentage">Completion %</option>
                    <option value="name">Name</option>
                    <option value="number">Number</option>
                    <option value="position">Position</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <option value="desc">High to Low</option>
                    <option value="asc">Low to High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                    value={positionFilter}
                    onChange={(e) => setPositionFilter(e.target.value)}
                  >
                    <option value="">All Positions</option>
                    {getPositions().map((position) => (
                      <option key={position} value={position}>
                        {position}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative" ref={groupDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Groups
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-left bg-white focus:outline-none focus:ring-2 focus:ring-slate-500 flex items-center justify-between"
                      onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                    >
                      <span className="text-gray-900">
                        {selectedGroups.length === 0
                          ? "Select groups..."
                          : `${selectedGroups.length} group${
                              selectedGroups.length > 1 ? "s" : ""
                            } selected`}
                      </span>
                      <ChevronDownIcon
                        className={`h-4 w-4 text-gray-400 transform transition-transform ${
                          showGroupDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showGroupDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {getGroups().length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No groups available
                          </div>
                        ) : (
                          <div className="py-1">
                            {getGroups().map((group) => (
                              <label
                                key={group}
                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="mr-2 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                                  checked={selectedGroups.includes(group)}
                                  onChange={() => handleGroupToggle(group)}
                                />
                                <span className="text-sm text-gray-900">
                                  {group}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedGroups.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedGroups.map((groupName) => (
                        <Badge
                          key={groupName}
                          variant="blue"
                          className="text-xs"
                        >
                          {groupName}
                          <button
                            className="ml-1 text-slate-800 hover:text-slate-900"
                            onClick={() => handleGroupToggle(groupName)}
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                      <button
                        onClick={() => setSelectedGroups([])}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Average Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category Scores
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedPlayers.map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {player.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {player.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            #{player.number} • {player.position || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getScoreColor(
                          getDisplayScore(player, "average_score"),
                          getOverallMaxScore(),
                        )}`}
                      >
                        {getDisplayScore(player, "average_score").toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-slate-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                player.completion_percentage || 0,
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                          {(player.completion_percentage || 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {player.completed_skills || 0} /{" "}
                        {player.total_skills || 0} skills
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {categories.slice(0, 3).map((category) => {
                          const categoryData =
                            player.category_scores?.[category.id];
                          if (!categoryData) return null;

                          const avgScore =
                            categoryData.skills_completed > 0
                              ? getDisplayScore(categoryData, "score") /
                                categoryData.skills_completed
                              : 0;

                          return (
                            <Badge
                              key={category.id}
                              variant={getBadgeColor(
                                avgScore,
                                getCategoryMaxScore(category.id),
                              )}
                              className="text-xs"
                            >
                              {category.name}: {avgScore.toFixed(1)}
                            </Badge>
                          );
                        })}
                        {categories.length > 3 && (
                          <Badge variant="gray" className="text-xs">
                            +{categories.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePlayerClick(player)}
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setParentReportPlayer(player);
                            setShowParentReport(true);
                          }}
                        >
                          <DocumentTextIcon className="w-4 h-4 mr-1" />
                          Parent Report
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {filteredAndSortedPlayers.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No players found
          </h3>
          <p className="text-gray-600">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}

        </>
      )}

      {/* Player Details Modal */}
      <PlayerDetailsModal
        player={selectedPlayer}
        scoreMode={scoreMode}
        categories={categories}
        skills={skills}
        isOpen={showPlayerDetails}
        onClose={() => {
          setShowPlayerDetails(false);
          setSelectedPlayer(null);
        }}
        expandedCategories={expandedCategories}
        onToggleCategory={toggleCategory}
        onParentReport={(player) => {
          setParentReportPlayer(player);
          setShowParentReport(true);
        }}
      />

      {/* Parent Report Modal */}
      <ParentReportModal
        isOpen={showParentReport}
        onClose={() => {
          setShowParentReport(false);
          setParentReportPlayer(null);
        }}
        evalId={evalId}
        playerId={parentReportPlayer?.id}
        playerName={parentReportPlayer?.name}
      />

      {/* Normalize Scores Confirmation Modal */}
      <Modal
        isOpen={showNormalizeConfirm}
        onClose={() => setShowNormalizeConfirm(false)}
        title="Normalize Scores"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            This will compute normalized scores for all players. Raw scores are preserved.
          </p>
          <div className="flex justify-end space-x-3 pt-2">
            <Button variant="outline" onClick={() => setShowNormalizeConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleNormalizeScores} disabled={normalizingScores}>
              {normalizingScores ? "Normalizing..." : "Normalize Scores"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Team Builder Panel */}
      <TeamBuilderPanel
        isOpen={showTeamBuilder}
        onClose={() => setShowTeamBuilder(false)}
        evalId={evalId}
        playerResults={playerResults}
        onSaveComplete={() => {
          loadData();
        }}
      />

      {/* Send Parent Reports Confirmation Modal */}
      <Modal
        isOpen={showSendReports}
        onClose={() => setShowSendReports(false)}
        title="Send Parent Reports"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            This will email evaluation report links to all players who have
            email addresses on file.
          </p>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-800">
              <strong>{playersWithEmails.length}</strong> of{" "}
              <strong>{playerResults.length}</strong> players have email
              addresses.
            </p>
          </div>
          {playersWithEmails.length === 0 ? (
            <p className="text-sm text-amber-600">
              No players have email addresses. Add emails to players first via
              the player edit form or CSV import.
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              Each player and their parents will receive a secure link to view
              the report. Links expire 14 days after the evaluation was
              completed.
            </p>
          )}
          <div className="flex justify-end space-x-3 pt-2">
            <Button variant="outline" onClick={() => setShowSendReports(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendParentReports}
              disabled={sendingReports || playersWithEmails.length === 0}
            >
              <EnvelopeIcon className="w-4 h-4 mr-2" />
              {sendingReports ? "Sending..." : "Send Report Emails"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Player Details Modal Component
const PlayerDetailsModal = ({
  player,
  scoreMode,
  categories,
  skills,
  isOpen,
  onClose,
  expandedCategories,
  onToggleCategory,
  onParentReport,
}) => {
  if (!player) return null;

  const getSkillScore = (skillId) => {
    return player.individual_scores?.find(
      (score) => score.skill_id === skillId,
    );
  };

  const getDisplayScore = (record, field) => {
    if (scoreMode === "normalized") {
      const normalizedField = field === "numeric_score" ? "normalized_score" : `normalized_${field}`;
      if (record?.[normalizedField] !== null && record?.[normalizedField] !== undefined) {
        return record[normalizedField];
      }
    }
    return record?.[field] || 0;
  };

  const getBadgeColor = (score, maxScore = 5) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "green";
    if (percentage >= 60) return "yellow";
    return "red";
  };

  // Get max score for a specific skill in the modal context
  const getModalSkillMaxScore = (skillId) => {
    const skill = skills.find((s) => s.id === skillId);
    if (skill?.rating_scales?.scale_type === "pass_fail") {
      return 10; // Pass/fail is converted to 10/0 in the backend
    }
    return skill?.rating_scales?.max_value || 5;
  };

  // Get max score for a category (average of all skills in that category)
  const getModalCategoryMaxScore = (categoryId) => {
    const categorySkills = skills.filter((s) => s.category_id === categoryId);
    if (categorySkills.length === 0) return 5;

    const maxScores = categorySkills.map((skill) => {
      if (skill?.rating_scales?.scale_type === "pass_fail") {
        return 10;
      }
      return skill?.rating_scales?.max_value || 5;
    });

    // Return average of max scores for the category
    return maxScores.reduce((sum, max) => sum + max, 0) / maxScores.length;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${player.name} - Detailed Results`}
      size="xl"
    >
      <div className="space-y-6">
        {/* Player Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Score</p>
              <p className="text-2xl font-bold text-gray-900">
                {getDisplayScore(player, "average_score").toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Score</p>
              <p className="text-2xl font-bold text-gray-900">
                {getDisplayScore(player, "total_score").toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Completion</p>
              <p className="text-2xl font-bold text-gray-900">
                {(player.completion_percentage || 0).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Skills</p>
              <p className="text-2xl font-bold text-gray-900">
                {player.completed_skills || 0}/{player.total_skills || 0}
              </p>
            </div>
          </div>
          {/* Parent Report Button */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => onParentReport(player)}
              className="flex items-center text-sm text-slate-600 hover:text-slate-800 font-medium"
            >
              <DocumentTextIcon className="w-4 h-4 mr-2" />
              Generate Parent Report
            </button>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            Category Breakdown
          </h3>

          {categories.map((category) => {
            const categoryData = player.category_scores?.[category.id];
            const isExpanded = expandedCategories.has(category.id);
            const categorySkills = skills.filter(
              (skill) => skill.category_id === category.id,
            );

            if (!categoryData) return null;

            const avgScore =
              categoryData.skills_completed > 0
                ? getDisplayScore(categoryData, "score") / categoryData.skills_completed
                : 0;

            return (
              <div
                key={category.id}
                className="border border-gray-200 rounded-lg"
              >
                <button
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                  onClick={() => onToggleCategory(category.id)}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color || "#3B82F6" }}
                    ></div>
                    <span className="font-medium text-gray-900">
                      {category.name}
                    </span>
                    <Badge
                      variant={getBadgeColor(
                        avgScore,
                        getModalCategoryMaxScore(category.id),
                      )}
                    >
                      {avgScore.toFixed(2)}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {categoryData.skills_completed}/{categorySkills.length}{" "}
                      skills
                    </span>
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-gray-100">
                    <div className="space-y-2 mt-3">
                      {categorySkills.map((skill) => {
                        const skillScore = getSkillScore(skill.id);

                        return (
                          <div
                            key={skill.id}
                            className="flex items-center justify-between py-2"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {skill.name}
                              </p>
                              {skill.description && (
                                <p className="text-xs text-gray-500">
                                  {skill.description}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              {skillScore ? (
                                <div>
                                  <Badge
                                    variant={getBadgeColor(
                                      getDisplayScore(skillScore, "numeric_score"),
                                      getModalSkillMaxScore(skill.id),
                                    )}
                                  >
                                    {getDisplayScore(skillScore, "numeric_score") ||
                                      skillScore.text_score ||
                                      (skillScore.pass_fail_score
                                        ? "Pass"
                                        : "Fail")}
                                  </Badge>
                                  {skillScore.notes && (
                                    <p className="text-xs text-gray-500 mt-1 max-w-32 truncate">
                                      {skillScore.notes}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <Badge variant="gray">Not Scored</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default SkillBasedResults;
